import React, { useEffect } from "react";
import {
  Box,
  Typography,
  IconButton,
  Button,
  CircularProgress,
  Card,
  CardContent,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import RoomIcon from "@mui/icons-material/Room";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import {
  getPendingRedeemRequest,
  cancelRedeemOrderRequest,
} from "../../redux/Redeem/redeemSlice";
import { useAppTheme } from "../../theme/useAppTheme";
import useCurrency from "../../hooks/useCurrency";
import LogoImage from "../../Screenshot_4.png";

const PendingRedeemScreen = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { id, type } = location.state || {};

  const { loading, pendingRedeemList, cancelResponse } = useSelector(
    (state) => state.redeem,
  );
  const { appConfig } = useSelector((state) => state.dashboard || {});

  useEffect(() => {
    if (id && type) {
      dispatch(getPendingRedeemRequest({ id, type }));
    }
  }, [id, type, dispatch]);

  const order = pendingRedeemList?.[0];
  const handleCancel = () => {
    if (order?._id) {
      dispatch(
        cancelRedeemOrderRequest({
          id: order._id,
          onSuccess: () => navigate("/RedeemHistory"),
        }),
      );
    }
  };

  const handleClose = () => {
    navigate("/RedeemHistory");
  };

  const themeColors = useAppTheme();
  const { symbol } = useCurrency();

  return (
    <Box
      sx={{
        background: themeColors.scaffoldGradientColors,
        minHeight: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        p: 2,
      }}
    >
      <Card
        sx={{
          width: "100%",
          maxWidth: 650,
          borderRadius: 4,
          boxShadow: 5,
          p: 3,
          position: "relative",
          background: themeColors.bigContainer,
          border: `1px solid ${themeColors.scaffold}`,
          color: themeColors.text,
        }}
      >
        <IconButton
          onClick={handleClose}
          sx={{
            position: "absolute",
            top: 12,
            right: 12,
            color: themeColors.text,
            background: "rgba(106,27,154,0.1)",
            "&:hover": { background: "rgba(106,27,154,0.2)" },
          }}
        >
          <CloseIcon />
        </IconButton>

        <Typography
          variant="h5"
          fontWeight={600}
          align="center"
          sx={{ mb: 3, color: themeColors.primary }}
        >
          Redeem Order
        </Typography>

        <CardContent
          sx={{
            background: themeColors.mediumContainer,
            border: `1px solid ${themeColors.scaffold}`,
            color: themeColors.text,
            borderRadius: 3,
            p: 3,
          }}
        >
          {loading ? (
            <Box textAlign="center" py={5}>
              <CircularProgress sx={{ color: "#fff" }} />
              <Typography mt={2}>Loading pending order...</Typography>
            </Box>
          ) : !order ? (
            <Typography align="center" sx={{ py: 5 }}>
              No pending order found.
            </Typography>
          ) : (
            <>
              <Box textAlign="center" mb={3}>
                <AccessTimeIcon sx={{ fontSize: 42, mb: 1 }} />
                <Typography fontWeight={600} fontSize="1.3rem">
                  Pending Order
                </Typography>
                <Typography fontSize="0.9rem" sx={{ opacity: 0.9 }}>
                  Please redeem your metal at the store within the time limit.
                </Typography>
                <Typography fontSize="0.9rem" sx={{ opacity: 0.9 }}>
                  Valid till{" "}
                  {new Date(order.createdAt + 20 * 60 * 1000).toLocaleString()}
                </Typography>
              </Box>

              <Box
                sx={{
                  background:
                    order.metalName === "Gold"
                      ? "linear-gradient(90deg, #FFD700, #C49B00)"
                      : "linear-gradient(90deg, #E0E0E0, #A9A9A9)",
                  color: "#000",
                  border: `1px solid ${themeColors.scaffold}`,
                  textAlign: "center",
                  borderRadius: "12px",
                  py: 1.2,
                  fontWeight: 700,
                  mb: 2,
                }}
              >
                {order.metalName.toUpperCase()} {order.bullion?.karat || "24"}K-
                {order.bullion?.purity || "999"}
              </Box>

              <Typography>
                <strong>Weight:</strong> {order.weight} gms
              </Typography>
              <Typography>
                <strong>Amount:</strong> {symbol}{order.amount}
              </Typography>
              <Typography>
                <strong>Share OTP at store:</strong>{" "}
                <Typography
                  component="span"
                  sx={{
                    display: "inline-block",
                    background: themeColors.bigContainer,
                    color: themeColors.text,
                    fontSize: "1.2rem",
                    fontWeight: 800,
                    px: 1.9,
                    py: 0.6,
                    borderRadius: "12px",
                    ml: 1,
                    letterSpacing: "3px",
                    boxShadow: "0 0 10px rgba(255,255,255,0.4)",
                  }}
                >
                  {order.otp}
                </Typography>
              </Typography>

              {/* Store Details */}
              <Box
                sx={{
                  mt: 3,
                  background: themeColors.mediumContainer,
                  borderRadius: "15px",
                  p: 2.5,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  textAlign: "center",
                  border: `1px solid ${themeColors.scaffold}`,
                }}
              >
                <Box
                  component="img"
                  src={
                    order.store?.logoUrl || appConfig?.logoUrl || LogoImage
                  }
                  alt="Store"
                  sx={{
                    width: 70,
                    height: 70,
                    borderRadius: "50%",
                    mb: 1,
                    objectFit: "cover",
                    border: "2px solid rgba(255,255,255,0.2)",
                  }}
                />
                <Typography fontWeight={600} fontSize="1rem">
                  {order.store?.branchName}
                </Typography>
                <Typography fontSize="0.9rem">
                  Phone: {order.store?.phone}
                </Typography>
                <Typography fontSize="0.9rem">
                  Code: {order.store?.branchCode}
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 1,
                    mt: 1,
                    maxWidth: "90%",
                  }}
                >
                  <RoomIcon sx={{ fontSize: 18, color: "#ffca28" }} />
                  <Typography fontSize="0.85rem">
                    {order.store?.address1}, {order.store?.address2},{" "}
                    {order.store?.address3} - {order.store?.pincode}
                  </Typography>
                </Box>
              </Box>

              <Button
                variant="contained"
                fullWidth
                sx={{
                  mt: 3,
                  background: themeColors.primary,
                  color: themeColors.text,
                  "&:hover": {
                    background: themeColors.svgColor,
                  },
                  border: `1px solid ${themeColors.scaffold}`,
                  textTransform: "none",
                  borderRadius: "30px",
                  py: 1.3,
                  fontWeight: 700,
                }}
                onClick={handleCancel}
              >
                {loading ? "Cancelling..." : "Cancel Order"}
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default PendingRedeemScreen;
